import { useEffect, useMemo, useState } from 'react';
import { useGetInsuranceCompanyList } from '../api/useGetInsuranceCompanyList';
import { usePickerSelect } from '../usePickerSelect';
import {
  HAS_DEDUCTIBLE,
  INSURANCE_USE,
  REPAIR_PLAN,
} from '@/consts/bodyAndPaint';
import { UpdateInsuranceInfoParams } from '@/types/api/bodyAndPaint';
import { InsuranceEntryItemType } from '@/types/bodyAndPaint';

interface InsuranceStateHookProps {
  ids?: {
    customerID: number;
    inspectionID: number;
    affiliateID: number;
  };
  initial?: InsuranceEntryItemType;
}
export const useInsuranceState = ({
  ids,
  initial,
}: InsuranceStateHookProps) => {
  const [insuranceState, setInsuranceState] = useState(
    {} as InsuranceEntryItemType,
  );
  useEffect(() => {
    initial &&
      setInsuranceState({
        ...initial,
        insuranceSelect: 0,
      });
  }, [initial]);
  const { insuranceCompanyList } = useGetInsuranceCompanyList();
  const [companyList, setCompanyList] = useState<
    { id: number; name: string }[]
  >([]);
  useEffect(() => {
    setCompanyList(insuranceCompanyList.insurance_companies);
  }, [insuranceCompanyList]);

  const insuranceSetState = useMemo(() => {
    return {
      insuranceSelect: (select: number) => {
        if (select === 0) {
          setInsuranceState((prev) => ({ ...prev, insuranceSelect: select }));
        } else {
          setInsuranceState({
            ...({} as InsuranceEntryItemType),
            insuranceSelect: select,
          });
        }
      },
      companyPick: (pick: string) => {
        const companyInfo = companyList?.find((item) => item.name === pick);
        setInsuranceState((prev) => {
          if (companyInfo) {
            return {
              ...prev,
              companyID: companyInfo.id,
              companyPick: companyInfo.name,
            };
          } else {
            return prev;
          }
        });
      },
      certificateText: (text: string) => {
        setInsuranceState((prev) => ({ ...prev, certificateText: text }));
      },
      receiptNumberText: (text: string) => {
        setInsuranceState((prev) => ({ ...prev, receiptNumberText: text }));
      },
      deductibleSelect: (select: number) => {
        setInsuranceState((prev) => {
          if (select === HAS_DEDUCTIBLE.NO.value) {
            return {
              ...prev,
              deductibleSelect: select,
              deductibleText: '0',
            };
          } else {
            return { ...prev, deductibleSelect: select };
          }
        });
      },
      deductibleText: (text: string) => {
        setInsuranceState((prev) => ({ ...prev, deductibleText: text }));
      },
      repairPlanSelect: (select: number) => {
        setInsuranceState((prev) => ({ ...prev, repairPlanSelect: select }));
      },
    };
  }, [companyList]);

  const insuranceSelectList = useMemo(() => {
    return {
      insuranceSelect: Object.values(INSURANCE_USE).map((item) => item.label),
      deductibleSelect: Object.values(HAS_DEDUCTIBLE).map((item) => item.label),
      repairPlanSelect: Object.values(REPAIR_PLAN).map((item) => item.label),
    };
  }, []);

  const insuranceError = useMemo(() => {
    const certificatePattern = '^[a-zA-Z0-9]*$';
    const receiptNumberPattern = '^[0-9]*$';
    const deductiblePattern = '^(0|[1-9]\\d*)$';
    return {
      certificateText: certificatePattern,
      receiptNumberText: receiptNumberPattern,
      deductibleText: deductiblePattern,
    };
  }, []);

  const companyPickList = useMemo(
    () => (companyList ? companyList.map((item) => item.name) : []),
    [companyList],
  );

  const picker = usePickerSelect({
    initialSelect: initial?.companyPick ?? '',
    selectList: companyPickList,
    pickerName: 'insurance-company',
  });
  const pickerInfo = useMemo(() => {
    return {
      inputID: picker.pickerInputID,
      containerID: picker.pickerContainerID,
      select: picker.pickerSelect,
    };
  }, [picker]);
  useEffect(() => {
    insuranceSetState.companyPick(pickerInfo.select);
  }, [pickerInfo.select]);

  const isInsurance = useMemo(() => {
    return ids ? true : insuranceState.insuranceSelect === 0;
  }, [ids, insuranceState.insuranceSelect]);
  const hasDeductible = useMemo(() => {
    return insuranceState.deductibleSelect === 0;
  }, [insuranceState.deductibleSelect]);

  const disabled = useMemo(() => {
    const {
      insuranceSelect,
      companyPick,
      certificateText,
      receiptNumberText,
      deductibleSelect,
      deductibleText,
      repairPlanSelect,
    } = insuranceState;

    const insuranceSelectCheck = !!(
      insuranceSelect !== undefined && insuranceSelect >= 0
    );
    const companyPickCheck = !!(
      companyPick !== undefined && companyPick !== ''
    );
    const certificateTextCheck = !!(
      certificateText !== undefined &&
      certificateText !== '' &&
      new RegExp(insuranceError.certificateText).test(certificateText)
    );
    const receiptNumberTextCheck = !!(
      receiptNumberText !== undefined &&
      receiptNumberText !== '' &&
      new RegExp(insuranceError.receiptNumberText).test(receiptNumberText)
    );
    const deductibleSelectCheck = !!(
      deductibleSelect !== undefined &&
      deductibleSelect >=
        Object.keys(HAS_DEDUCTIBLE).findIndex((key) => key === 'YES')
    );
    const deductibleTextCheck =
      deductibleSelect ===
      Object.keys(HAS_DEDUCTIBLE).findIndex((key) => key === 'YES')
        ? !!(
            deductibleText !== undefined &&
            deductibleText !== '' &&
            new RegExp(insuranceError.deductibleText).test(deductibleText)
          )
        : true;
    const repairPlanSelectCheck = !!(
      repairPlanSelect !== undefined &&
      repairPlanSelect >= REPAIR_PLAN.YES.value
    );

    return isInsurance
      ? !(
          insuranceSelectCheck &&
          companyPickCheck &&
          certificateTextCheck &&
          receiptNumberTextCheck &&
          deductibleSelectCheck &&
          deductibleTextCheck &&
          repairPlanSelectCheck
        )
      : !insuranceSelectCheck;
  }, [isInsurance, insuranceState, insuranceError]);

  const postParameters = useMemo(() => {
    const {
      customerID: m_customer_id,
      inspectionID: t_pm_car_inspection_id,
      affiliateID: m_affiliate_id,
    } = ids ?? {};
    const {
      insuranceSelect,
      companyPick,
      certificateText,
      receiptNumberText,
      deductibleSelect,
      deductibleText,
      repairPlanSelect,
    } = insuranceState;

    const insurance =
      Object.values(INSURANCE_USE).find((_, idx) => idx === insuranceSelect)
        ?.value ?? 0;
    const deductible =
      Object.values(HAS_DEDUCTIBLE).find((_, idx) => idx === deductibleSelect)
        ?.value ?? 0;
    const repairPlan =
      Object.values(REPAIR_PLAN).find((_, idx) => idx === repairPlanSelect)
        ?.value ?? 0;
    const companyID = companyList
      ? companyList.find((item) => item.name === companyPick)?.id ?? 0
      : 0;

    return {
      m_customer_id: m_customer_id ?? 0,
      t_pm_car_inspection_id: t_pm_car_inspection_id ?? 0,
      m_affiliate_id: m_affiliate_id ?? 0,
      insurance_info: {
        insurance_use_flg: insurance,
        m_insurance_company_id: isInsurance ? companyID : undefined,
        policy_number: isInsurance ? certificateText : undefined,
        has_deductible: isInsurance ? deductible : undefined,
        deductible_fee: isInsurance ? deductibleText : undefined,
        repair_planning: isInsurance ? repairPlan : undefined,
        accident_reception_number: isInsurance ? receiptNumberText : undefined,
      },
    } as UpdateInsuranceInfoParams;
  }, [ids, isInsurance, companyList, insuranceState]);

  return {
    insuranceState,
    insuranceSetState,
    insuranceSelectList,
    insuranceError,
    pickerInfo,
    disabled,
    hasDeductible,
    isInsurance,
    postParameters,
  };
};
